import { inject, Injectable } from '@angular/core';
import { AppConfigService } from '@data-terminal/data-access/app-config/app-config.service';
import { APP_RUNTIME, AppRuntime } from '@data-terminal/data-access/injection-tokens';
import { UserMachinesService } from '@data-terminal/data-access/user-machines';
import { OnlinePressNavigationService } from '@data-terminal/feature-online-press-web-component/services/online-press-navigation.service';
import { CaptureMode, isLightModeMachine, Machine, UserMachines } from '@data-terminal/shared-models/machine';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, delay, interval, map, Observable, Subject, switchMap, takeWhile, tap } from 'rxjs';
import { InactivityDialogManagerService } from './inactivity-dialog-manager.service';

export const KEY_MACHINE_INACATIVE = 'KEY_MACHINE_INACATIVE';

const ONE_SECOND = 1000;

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class InactivityTrackerService {
    private readonly inactivityDialogManagerService = inject(InactivityDialogManagerService);
    private readonly appConfigService = inject(AppConfigService);
    private readonly userMachinesService = inject(UserMachinesService);
    private readonly onlinePressNavigationService = inject(OnlinePressNavigationService);
    private readonly appRuntime = inject<AppRuntime>(APP_RUNTIME);

    constructor() {
        this.inactiveWorkstations$
            .pipe(untilDestroyed(this), this.handleActiveWorkstations, this.handleInactiveWorkstations)
            .subscribe();
    }

    private readonly PROD_TIMER_THRESHOLD_MINUTES = 5;
    private readonly OTHER_TIMER_THRESHOLD_MINUTES = 15;

    private readonly TIMER_THRESHOLD_MINUTES =
        this.appConfigService.getStage() === 'prod'
            ? this.PROD_TIMER_THRESHOLD_MINUTES
            : this.OTHER_TIMER_THRESHOLD_MINUTES;

    private readonly inactiveWorkstationsTimersMap: Map<string, Observable<number>> = new Map();
    private readonly inactiveWorkstationsOffsetMap: Map<string, number> = new Map();

    private readonly userMachines$: Observable<UserMachines> = this.userMachinesService.getUserMachines$();

    private readonly mapToInactiveWorkstations = (stream$: Observable<UserMachines>): Observable<Machine[]> =>
        stream$.pipe(map((data) => data.assignedMachines.filter(this.filterForInactiveWorkstations.bind(this))));

    private readonly inactiveWorkstations$: Observable<Machine[]> = interval(ONE_SECOND).pipe(
        switchMap(() => this.userMachines$),
        this.mapToInactiveWorkstations
    );

    private readonly handleActiveWorkstations = (stream$: Observable<Machine[]>): Observable<Machine[]> =>
        stream$.pipe(
            tap((inactiveWorkstations) => {
                [...this.inactiveWorkstationsTimersMap.keys()].forEach((machineId) => {
                    if (!inactiveWorkstations.some((machine) => machine.machineId === machineId)) {
                        this.inactiveWorkstationsTimersMap.delete(machineId);
                        this.inactivityDialogManagerService.removeInactiveMachine(machineId);
                    }
                });
            })
        );

    private readonly handleInactiveWorkstations = (stream$: Observable<Machine[]>): Observable<Machine[]> =>
        stream$.pipe(
            tap((inactiveWorkstations) => {
                inactiveWorkstations.forEach((machine) => {
                    if (!this.inactiveWorkstationsTimersMap.has(machine.machineId)) {
                        const { machineId } = machine;

                        this.markMachineUnchecked(machineId);
                        const lastActiveTimestamp = this.lastActiveTimestamp(machine);
                        const secondsSinceInactive = Math.max(Date.now() - lastActiveTimestamp, 0);
                        const inactivityTimerSubj$ = new BehaviorSubject(secondsSinceInactive);
                        const inactivityTimer$ = inactivityTimerSubj$.asObservable();
                        this.inactiveWorkstationsTimersMap.set(machineId, inactivityTimer$);
                        this.inactiveWorkstationsOffsetMap.set(machineId, 0);
                        this.startTimer(machineId, inactivityTimer$, inactivityTimerSubj$, lastActiveTimestamp);

                        // If the aggregating dialog is already open, surface the newly
                        // inactive machine immediately so the dialog keeps showing every
                        // inactive machine, not only those that crossed the threshold.
                        if (this.inactivityDialogManagerService.isDialogOpen()) {
                            this.markMachineChecked(machineId);
                            this.inactivityDialogManagerService.addInactiveMachine(machineId, inactivityTimer$);
                        }
                    }
                });
            })
        );

    public getInactiveWorkstations$(): Observable<Machine[]> {
        return this.inactiveWorkstations$;
    }

    public getInactiveWorkstations(): Machine[] {
        return this.userMachinesService.getAssignedMachines().filter(this.filterForInactiveWorkstations.bind(this));
    }

    public getInactiveWorkstationsTimersMap(): Map<string, Observable<number>> {
        return this.inactiveWorkstationsTimersMap;
    }

    private markMachineChecked(machineId: string): void {
        localStorage.setItem(`${KEY_MACHINE_INACATIVE}_${machineId}`, JSON.stringify(true));
    }

    public markMachineUnchecked(machineId: string): void {
        localStorage.setItem(`${KEY_MACHINE_INACATIVE}_${machineId}`, JSON.stringify(false));
    }

    public updateTimerOffset(machineId: string, inactiveSince?: number): void {
        if (!inactiveSince) {
            const machine = this.userMachinesService
                .getAssignedMachines()
                .find((assignedMachine) => assignedMachine.machineId === machineId);
            if (!machine) {
                throw new Error('No machine found for inactivity tracking!');
            }
            inactiveSince = this.lastActiveTimestamp(machine);
        }
        this.inactiveWorkstationsOffsetMap.set(machineId, Date.now() - inactiveSince);
    }

    private filterForInactiveWorkstations(machine: Machine): boolean {
        const baseFilter =
            machine.runningOperation?.opName === undefined &&
            machine.captureMode === CaptureMode.START_STOP &&
            !isLightModeMachine(machine);

        // In Online-Press Web Component we care only about one machine
        if (this.appRuntime === AppRuntime.ONLINE_PRESS_WEB_COMPONENT) {
            return baseFilter && machine.machineId === this.onlinePressNavigationService.deviceId;
        }

        return baseFilter;
    }

    private getMachineCheckedState(machineId: string): boolean {
        const checkedReturn = localStorage.getItem(`${KEY_MACHINE_INACATIVE}_${machineId}`);
        return checkedReturn ? JSON.parse(checkedReturn) : false;
    }

    private lastActiveTimestamp(machine: Machine): number {
        const { signedOnUsers } = machine;
        const captain = signedOnUsers.find((user) => user.captain);
        const captainLastActiveTimestamp = Math.max(captain?.timestamp || 0, captain?.inactiveSince || 0);
        return captainLastActiveTimestamp || Date.now();
    }

    private startTimer(
        machineId: string,
        inactivityTimer$: Observable<number>,
        inactivityTimerSubj$: Subject<number>,
        inactiveSince: number
    ): void {
        inactivityTimer$
            .pipe(
                takeWhile(() => this.inactiveWorkstationsTimersMap.has(machineId)),
                untilDestroyed(this),
                delay(ONE_SECOND)
            )
            .subscribe(() => {
                const newTime = Math.max(Date.now() - inactiveSince, 0);
                inactivityTimerSubj$.next(newTime);
                // Flag the machine for the inactivity dialog once it has been
                // inactive for at least the configured threshold (plus offset).
                const checked = this.getMachineCheckedState(machineId);
                const timerOffset = this.inactiveWorkstationsOffsetMap.get(machineId) || 0;
                if (newTime >= this.TIMER_THRESHOLD_MINUTES * 60 * ONE_SECOND + timerOffset && !checked) {
                    this.showAllInactiveMachines();
                }
            });
    }

    // As soon as any machine crosses the inactivity threshold, surface every
    // currently inactive machine in the single dialog at once. They are all
    // reset together when the dialog is closed.
    private showAllInactiveMachines(): void {
        this.inactiveWorkstationsTimersMap.forEach((timer$, id) => {
            this.markMachineChecked(id);
            this.inactivityDialogManagerService.addInactiveMachine(id, timer$);
        });
    }
}
